/**
 * Hook useChat — orquestra conexões, conversas e mensagens para o ChatContext.
 */
import { useState, useCallback, useMemo, useRef } from 'react';
import { useChatConnection } from './useChatConnection';
import { useChatConversations } from './useChatConversations';
import { useChatMessages } from './useChatMessages';
import { useChatPushDeepLink } from './useChatAutoStart';
import type { ContactCardInput, LocationMessageInput, PixMessageInput } from './useChatSpecialActions';
import { classifyError } from '../../../utils/networkError';
import {
    getInstagramReplyWindowError,
    getOutboundChatSendError,
    getWhatsappCloudReplyWindowError,
} from '../lib/chatSendGuard';
import {
    autoAssignConversationOnSend,
    resolveChatAgentDisplayName,
} from '../lib/chatAutoAssign';
import { useAuth } from '../../../contexts/AuthContext';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useUsers } from '../../../contexts/users/UsersContext';
import { findConversation, conversationIdentityMatch } from '../lib/conversationScope';
import type { SendQuotedRef } from './types';
export * from './types';

type FailedSend = { text: string; quotedMessage?: SendQuotedRef };

export const useChat = () => {
    const { user } = useAuth();
    const { currentOrganization } = useOrganization();
    const { crmUsers } = useUsers();
    const currentUserProfile = useMemo(
        () => crmUsers.find((u) => u.id === user?.id),
        [crmUsers, user?.id],
    );

    const {
        connections, selectedConnectionId, setSelectedConnectionId,
        selectedConnection, isLoadingConnections, refreshConnections, whatsappLive,
    } = useChatConnection();

    // Filtro de canal da sidebar → query server-side (evita auto-load storm WA+IG misturados).
    const [inboxChannelConnectionId, setInboxChannelConnectionId] = useState<string | null>(null);

    const {
        conversations, setConversations, isLoadingConversations,
        startNewConversation: startNewConv, assignConversation,
        transferConversation, removeAssignment, closeConversation,
        reopenConversation, updateConversation, currentUserId,
        isLoadingMoreConversations, hasMoreConversations, loadMoreConversations,
        backendCounters,
    } = useChatConversations(inboxChannelConnectionId, selectedConnectionId);

    const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
    /** Linha WA/IG aberta — evita misturar twin com o mesmo telefone. */
    const [activeConnectionId, setActiveConnectionId] = useState<string | null>(null);

    const selectConversation = useCallback((id: string | null, connectionId?: string | null) => {
        setActiveConversationId(id);
        setActiveConnectionId(id ? (connectionId ?? null) : null);
    }, []);
    const [error, setError] = useState<string | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);
    const lastFailedMessageRef = useRef<FailedSend | null>(null);
    const triggerAutoAssignRef = useRef<() => void>(() => {});

    const handleSendFailure = useCallback((err: unknown, text: string, quotedMessage?: SendQuotedRef) => {
        const classified = classifyError(err);
        setError(classified.message);
        if (classified.isRetryable) lastFailedMessageRef.current = { text, quotedMessage };
    }, []);

    const activeConvMeta = useMemo(() => {
        if (!activeConversationId) return undefined;
        return findConversation(
            conversations,
            activeConversationId,
            activeConnectionId ?? inboxChannelConnectionId,
        );
    }, [conversations, activeConversationId, activeConnectionId, inboxChannelConnectionId]);

    const activePlatform = activeConvMeta?.platform
        ?? (activeConversationId?.startsWith('ig:') ? 'instagram' : 'whatsapp');
    const isInstagramActive = activePlatform === 'instagram' || Boolean(activeConversationId?.startsWith('ig:'));
    const conversationConnection = connections.find((connection) => connection.id === activeConvMeta?.connectionId);
    // Instagram: nunca herda connectionId/WhatsApp selecionado — filtra mensagens erradas e trava o painel
    const messagingConnection = isInstagramActive ? conversationConnection : (conversationConnection ?? selectedConnection);
    const activeMessagingConnectionId = isInstagramActive
        ? (activeConvMeta?.connectionId ?? activeConnectionId ?? null)
        : (activeConvMeta?.connectionId ?? activeConnectionId ?? selectedConnection?.id ?? null);
    const officialApi = Boolean(!isInstagramActive && messagingConnection?.officialApi);
    const activeInstanceName = activePlatform === 'whatsapp' && !officialApi
        ? messagingConnection?.instanceName
        : undefined;

    const {
        messages, isLoading: isLoadingMessages,
        sendMessage: sendMsg, sendMedia: sendMediaMsg,
        sendContactCard: sendContactCardMsg, sendLocationMessage: sendLocationMsg, sendPixMessage: sendPixMsg,
        deleteMessage: deleteMsg, editMessage: editMsg,
        refreshMessages, loadOlderMessages, isLoadingOlder, hasMoreMessages,
        isPeerTyping, sendTyping,
    } = useChatMessages(
        activeInstanceName,
        activeConversationId,
        activeMessagingConnectionId,
        activePlatform,
        handleSendFailure,
        officialApi,
        () => { triggerAutoAssignRef.current(); },
    );

    const activeConversation = useMemo(
        () => (activeConvMeta ? { ...activeConvMeta, messages } : undefined),
        [activeConvMeta, messages],
    );
    useChatPushDeepLink(currentOrganization?.id, selectConversation);

    const patchConversationPreview = useCallback((conversationId: string, lastMessage: string, touchTime: boolean) => {
        const scopeId = activeMessagingConnectionId;
        setConversations((prev) => prev.map((c) => (
            conversationIdentityMatch(c, conversationId, scopeId)
                ? { ...c, lastMessage, ...(touchTime ? { lastMessageTime: new Date() } : {}) }
                : c
        )));
    }, [setConversations, activeMessagingConnectionId]);

    const triggerAutoAssign = useCallback(() => {
        if (!activeConversationId || !user || !currentOrganization) return;
        autoAssignConversationOnSend({
            conversationId: activeConversationId,
            organizationId: currentOrganization.id,
            displayName: resolveChatAgentDisplayName(currentUserProfile?.name, user.user_metadata),
            messagingConnectionId: activeMessagingConnectionId,
            assignConversation,
            refreshMessages,
        }).catch((err) => console.error('[AutoAssign] Erro crítico:', err));
    }, [activeConversationId, user, currentOrganization, currentUserProfile, activeMessagingConnectionId, assignConversation, refreshMessages]);
    triggerAutoAssignRef.current = triggerAutoAssign;

    const ensureCanSend = useCallback((opts?: { throwOnError?: boolean }) => {
        const caresAboutWindow = activePlatform === 'instagram' || officialApi;
        const lastInboundAt = caresAboutWindow
            ? [...messages].reverse().find((message) => message.sender === 'them')?.timestamp.getTime() ?? null
            : null;
        const sendError = getOutboundChatSendError({
            hasActiveConversation: Boolean(activeConversationId),
            platform: activePlatform,
            hasWhatsAppInstance: Boolean(messagingConnection?.instanceName),
            hasMessagingConnection: Boolean(activeMessagingConnectionId),
            officialApi,
            whatsappLive,
        }) ?? (activePlatform === 'instagram'
            // IG: só avalia a janela depois do fetch — histórico vazio NÃO libera envio.
            ? getInstagramReplyWindowError(lastInboundAt, !isLoadingMessages)
            : officialApi
                // WA Cloud: intacto — bypass enquanto o histórico ainda não carregou.
                ? getWhatsappCloudReplyWindowError(lastInboundAt, messages.length > 0)
                : null);
        if (sendError) {
            setError(sendError);
            if (opts?.throwOnError) throw new Error(sendError);
            return false;
        }
        setError(null);
        return true;
    }, [
        activeConversationId,
        activePlatform,
        messagingConnection?.instanceName,
        activeMessagingConnectionId,
        officialApi,
        whatsappLive,
        messages,
        isLoadingMessages,
    ]);

    const completeSend = useCallback(async (send: () => Promise<void>, rethrow: boolean) => {
        try {
            await send();
            triggerAutoAssign();
        } catch (err: unknown) {
            const classified = classifyError(err);
            setError(classified.message);
            // Uncertain/pending: nunca guarda para "Tentar novamente" (risco de duplicar).
            if (!classified.isRetryable) lastFailedMessageRef.current = null;
            if (rethrow) throw err;
        }
    }, [triggerAutoAssign]);

    const startNewConversation = useCallback((number: string, name: string) => {
        startNewConv(number, name).then((jid) => {
            selectConversation(jid, selectedConnectionId);
        }).catch((err) => {
            setError('Erro ao iniciar conversa: ' + err.message);
        });
    }, [startNewConv, selectConversation, selectedConnectionId]);

    const sendMessage = useCallback((text: string, quotedMessage?: SendQuotedRef) => {
        const trimmed = text.trim();
        if (!trimmed) { setError('Digite uma mensagem antes de enviar.'); return false; }
        if (!ensureCanSend()) return false;
        if (sendMsg(trimmed, quotedMessage) === false) return false;
        lastFailedMessageRef.current = null;
        if (activeConversationId) patchConversationPreview(activeConversationId, trimmed, true);
        // Auto-assign sobe via onSendSucceeded após confirmação do provedor.
        return true;
    }, [activeConversationId, ensureCanSend, sendMsg, patchConversationPreview]);

    const retryLastMessage = useCallback(async () => {
        const lastFailed = lastFailedMessageRef.current;
        if (!lastFailed) return;
        setIsRetrying(true);
        try { await sendMessage(lastFailed.text, lastFailed.quotedMessage); }
        finally { setIsRetrying(false); }
    }, [sendMessage]);

    const sendMedia = useCallback(async (file: File, caption?: string) => {
        if (!ensureCanSend()) return;
        await completeSend(() => sendMediaMsg(file, caption), false);
    }, [ensureCanSend, sendMediaMsg, completeSend]);

    const runSpecialSend = useCallback(async <T,>(send: (input: T) => Promise<void>, input: T) => {
        ensureCanSend({ throwOnError: true });
        await completeSend(() => send(input), true);
    }, [ensureCanSend, completeSend]);

    const sendContactCard = useCallback(
        (input: ContactCardInput) => runSpecialSend(sendContactCardMsg, input),
        [runSpecialSend, sendContactCardMsg],
    );
    const sendLocationMessage = useCallback(
        (input: LocationMessageInput) => runSpecialSend(sendLocationMsg, input),
        [runSpecialSend, sendLocationMsg],
    );
    const sendPixMessage = useCallback(
        (input: PixMessageInput) => runSpecialSend(sendPixMsg, input),
        [runSpecialSend, sendPixMsg],
    );

    const editMessage = useCallback((messageId: string, newText: string, originalText?: string) => {
        const trimmed = newText.trim();
        if (activeConversationId && trimmed && messages[messages.length - 1]?.id === messageId) {
            patchConversationPreview(activeConversationId, trimmed, false);
        }
        editMsg(messageId, newText, originalText);
    }, [activeConversationId, messages, editMsg, patchConversationPreview]);

    const clearError = useCallback(() => {
        setError(null);
        lastFailedMessageRef.current = null;
    }, []);
    const isLoading = isLoadingConnections || isLoadingConversations;

    return useMemo(() => ({
        connections, selectedConnectionId, setSelectedConnectionId, selectedConnection,
        conversations, activeConversation, activeConversationId, activeConnectionId,
        setActiveConversationId: selectConversation,
        startNewConversation, sendMessage, sendMedia, sendContactCard, sendLocationMessage, sendPixMessage,
        deleteMessage: deleteMsg, editMessage, assignConversation, transferConversation, removeAssignment,
        closeConversation, reopenConversation, updateConversation, currentUserId,
        isLoading, isLoadingConnections, isLoadingMessages, error, clearError, retryLastMessage, isRetrying,
        setSelectedConnection: setSelectedConnectionId, refreshConnections,
        loadOlderMessages, isLoadingOlder, hasMoreMessages,
        isLoadingMoreConversations, hasMoreConversations, loadMoreConversations,
        backendCounters, whatsappLive, isPeerTyping, sendTyping,
        inboxChannelConnectionId, setInboxChannelConnectionId,
    }), [
        connections, selectedConnectionId, setSelectedConnectionId, selectedConnection,
        conversations, activeConversation, activeConversationId, activeConnectionId, selectConversation,
        startNewConversation, sendMessage, sendMedia,
        sendContactCard, sendLocationMessage, sendPixMessage, deleteMsg, editMessage,
        assignConversation, transferConversation, removeAssignment, closeConversation, reopenConversation,
        updateConversation, currentUserId, isLoading, isLoadingConnections, isLoadingMessages, error, clearError,
        retryLastMessage, isRetrying, refreshConnections, loadOlderMessages, isLoadingOlder, hasMoreMessages,
        isLoadingMoreConversations, hasMoreConversations, loadMoreConversations,
        backendCounters, whatsappLive, isPeerTyping, sendTyping,
        inboxChannelConnectionId, setInboxChannelConnectionId,
    ]);
};
